import { useState } from "react";
import {
  X,
  Loader2,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  CalendarDays,
} from "lucide-react";
import {
  useAdminGradeUpgradePreviewQuery,
  useAdminApplyGradeUpgradeMutation,
} from "../api/upgradeApi";

/**
 * Conferring the grade — the step after numbering.
 *
 * Issuing a number puts the new number on the register BESIDE the one the
 * member already holds. This is what makes it the number in force, files the
 * old one under the grade it belonged to, and moves the member and their
 * account to the new grade.
 *
 * Two steps for the same reason the numbering dialog has two: nothing here can
 * be undone by clicking the opposite button, so the list of exactly who will be
 * changed, and what their number will become, is shown before anything is
 * written. The server decides who is eligible — two copies of that reasoning
 * would eventually disagree, and the one that lost would have already re-graded
 * people.
 */
export default function ApplyGradeUpgradeModal({
  categoryId,
  categoryName,
  dietId,
  dietTitle,
  onClose,
}: {
  categoryId: string | number;
  /** The grade being conferred — "Member", "Fellow", "Probationer". */
  categoryName: string;
  /** Narrow the batch to one review cycle's applicants. */
  dietId?: string;
  dietTitle?: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useAdminGradeUpgradePreviewQuery({
    to_category_id: categoryId,
    review_diet_id: dietId,
  });

  const [apply, { isLoading: applying }] = useAdminApplyGradeUpgradeMutation();

  const [result, setResult] = useState<any>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");

  const preview = data?.data ?? data;
  const eligible: any[] = preview?.eligible ?? [];
  const skipped = preview?.skipped ?? {};
  const unnumbered: any[] = skipped?.no_number_issued ?? [];
  const orphans: any[] = skipped?.no_member_record ?? [];
  // Their grade has moved since they applied — already carried up by this
  // button, or changed elsewhere. Either way this cycle's decision no longer
  // describes them.
  const moved: any[] = skipped?.grade_changed ?? [];
  // The date these members will be recorded as having reached the grade. An
  // admin sets it on the review cycle; there is no default, and without it the
  // server refuses the upgrade. Shown either way — it is what their
  // certificates are dated from.
  const conferredOn: string | undefined = preview?.conferred_on ?? undefined;
  const blockedReason: string | undefined = preview?.blocked_reason ?? undefined;

  const doApply = async () => {
    setFailure(null);
    try {
      const res = await apply({
        to_category_id: categoryId,
        review_diet_id: dietId,
      }).unwrap();
      setResult(res);
    } catch (e: any) {
      setFailure(
        e?.data?.message ?? e?.error ?? "Could not complete the upgrade. Nothing was written.",
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-emerald-600" />
              Upgrade to {categoryName}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Approved applicants who already hold their new number.
              {dietId ? ` In “${dietTitle ?? "this review cycle"}” only.` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {isLoading && (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                {(error as any)?.data?.message ?? "Could not load the upgrade preview."}
                <button onClick={() => refetch()} className="ml-2 underline font-semibold">
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── After upgrading ──────────────────────────────────────────── */}
          {result && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                {result.message}
              </div>

              {(result.data?.upgraded?.length ?? 0) > 0 && (
                <UpgradedTable rows={result.data.upgraded} />
              )}

              {(result.data?.failed?.length ?? 0) > 0 && (
                <SkippedList
                  title={`Could not be completed (${result.data.failed.length})`}
                  rows={result.data.failed}
                  render={(r) => `${r.name} · ${r.error}`}
                  tone="warn"
                />
              )}

              <p className="text-xs text-gray-500">
                Their member record and their login account are both on the new grade, dated{" "}
                {formatDate(result.data?.conferred_on) || "the cycle's date"}. The number they held
                before is kept on the register, under the grade they held it at.
              </p>
            </div>
          )}

          {/* ── Before upgrading ─────────────────────────────────────────── */}
          {!result && preview && (
            <div className="space-y-5">
              {/* The date, or what to go and do about it. Said before the list
                  rather than at the moment the button is pressed — the admin
                  can set it on the cycle and come straight back. */}
              {conferredOn ? (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-gray-50 border text-sm text-gray-700">
                  <CalendarDays className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                  <div>
                    They will be recorded as reaching {categoryName} on{" "}
                    <b className="text-gray-900">{formatDate(conferredOn)}</b> — the cycle&rsquo;s
                    Date conferred. Change it on the cycle if that is wrong.
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  <CalendarDays className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="font-semibold">
                    {blockedReason ??
                      "This review cycle has no Date conferred set. Set it on the cycle first."}
                  </div>
                </div>
              )}

              {eligible.length > 0 ? (
                <div>
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Will be upgraded ({eligible.length})
                  </div>
                  <div className="border rounded-xl overflow-hidden max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr className="text-left text-gray-500 border-b">
                          <th className="px-3 py-2 font-medium">Applicant</th>
                          <th className="px-3 py-2 font-medium">Grade</th>
                          <th className="px-3 py-2 font-medium">Number</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {eligible.map((row) => (
                          <tr key={row.application_id}>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900">{row.name || "—"}</div>
                              <div className="text-xs text-gray-400">{row.email}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                              {row.from_category ?? "—"}
                              <ArrowRight className="w-3 h-3 inline mx-1 text-gray-400" />
                              <b className="text-gray-900">{row.to_category}</b>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap font-mono">
                              <span className="text-gray-400 line-through">
                                {row.membership_no || "—"}
                              </span>
                              <ArrowRight className="w-3 h-3 inline mx-1 text-gray-400" />
                              <b className="text-emerald-700">{row.new_membership_no || "—"}</b>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl border bg-gray-50 text-sm text-gray-700">
                  Nobody is ready to be upgraded. An applicant has to be approved and already
                  holding their new number.
                </div>
              )}

              {/* Passed over, and why — the same principle as the numbering
                  dialog: a count that quietly excluded these would hide the
                  step somebody still has to take. */}
              {unnumbered.length > 0 && (
                <SkippedList
                  title={`No number issued yet — issue numbers first (${unnumbered.length})`}
                  rows={unnumbered}
                  render={(r) => `${r.name} · ${r.membership_no ?? "no number"}`}
                  tone="warn"
                />
              )}
              {moved.length > 0 && (
                <SkippedList
                  title={`Grade already changed — skipped (${moved.length})`}
                  rows={moved}
                  render={(r) =>
                    `${r.name} · now ${r.current_grade ?? "a different grade"}` +
                    ` (applied from ${r.from_category ?? "—"})`
                  }
                />
              )}
              {orphans.length > 0 && (
                <SkippedList
                  title={`No members record — cannot be upgraded (${orphans.length})`}
                  rows={orphans}
                  render={(r) => `${r.name} · ${r.email ?? "no email"}`}
                  tone="warn"
                />
              )}

              {failure && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  {failure}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-between gap-3 flex-wrap">
          {!result && eligible.length > 0 ? (
            <>
              <label className="text-xs text-gray-600 flex items-center gap-2">
                Type <b className="font-mono">UPGRADE</b> to confirm
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  className="w-28 px-2 py-1 border rounded-md text-sm font-mono outline-none focus:ring-2 focus:ring-emerald-100"
                  placeholder="UPGRADE"
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={doApply}
                  title={conferredOn ? undefined : "Set the cycle's Date conferred first."}
                  disabled={applying || confirmText !== "UPGRADE" || !conferredOn}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {applying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <GraduationCap className="w-4 h-4" />
                  )}
                  Upgrade {eligible.length} member{eligible.length === 1 ? "" : "s"}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A plain Y-m-d as a date a reader can check against a minute book. */
function formatDate(value?: string | null): string {
  if (!value) return "";

  const d = new Date(`${value}T00:00:00`);

  if (isNaN(d.getTime())) return value;

  return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

function SkippedList({
  title,
  rows,
  render,
  tone = "plain",
}: {
  title: string;
  rows: any[];
  render: (row: any) => string;
  tone?: "plain" | "warn";
}) {
  return (
    <details
      className={`rounded-xl border px-3 py-2 ${
        tone === "warn" ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50"
      }`}
    >
      <summary className="text-xs font-bold text-gray-700 cursor-pointer">{title}</summary>
      <ul className="mt-2 space-y-1 text-xs text-gray-600 max-h-40 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.application_id}>{render(r)}</li>
        ))}
      </ul>
    </details>
  );
}

function UpgradedTable({ rows }: { rows: any[] }) {
  return (
    <div className="border rounded-xl overflow-hidden max-h-72 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="text-left text-gray-500 border-b">
            <th className="px-3 py-2 font-medium">Member</th>
            <th className="px-3 py-2 font-medium">Now</th>
            <th className="px-3 py-2 font-medium">Previous number</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.application_id}>
              <td className="px-3 py-2 font-medium text-gray-900">{r.name}</td>
              <td className="px-3 py-2 font-mono font-bold text-emerald-700">
                {r.membership_no}
              </td>
              {/* Where the old number went, named — it is the thing a reader
                  will look for when somebody quotes an old receipt. */}
              <td className="px-3 py-2 text-gray-500 font-mono">
                {r.previous_no ?? "—"}
                {r.kept_in ? <span className="ml-1 text-[11px] not-italic">({r.kept_in})</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
